import { chunk } from 'lodash-es';
import { Logger } from '@front/Logger';

type TDataLayerEventCommand = {
    [key: string]: unknown;
    eventCommand: string;
};

type TDataLayerProduct = {
    [key: string]: unknown;
    productId: string;
    productName: string;
};

type TDataLayerPromotion = {
    [key: string]: unknown;
    promotionId: string;
    promotionName: string;
};

/**
 * Usage :
 *
 * ```
 *     import { DataLayer } from '@front/services/datalayer';
 *
 *     declare global {
 *         interface Window {
 *             dataLayer: any[],
 *             gtag: Function,
 *             gadl: DataLayer,
 *         }
 *     }
 *
 *     const gadl = new DataLayer('project-name');
 *
 *     window.dataLayer = window.dataLayer || [];
 *     window.gtag = gadl.push;
 *     window.gadl = gadl;
 * ```
 */
class DataLayer {
    readonly EVENT_PREFIX: string;

    readonly logger: Logger = new Logger('DATALAYER', 'info');

    readonly delayedProductImpressionsChunkSize = 25;
    readonly delayedPromotionImpressionsChunkSize = 25;

    readonly delayedProductImpressionsInterval = 250;
    readonly delayedPromotionImpressionsInterval = 250;

    readonly delayedProductImpressions: Map<string, TDataLayerProduct[]> = new Map<string, TDataLayerProduct[]>();
    readonly delayedPromotionImpressions: Map<string, TDataLayerPromotion[]> = new Map<string, TDataLayerPromotion[]>();

    readonly delayedProductImpressionsLoop: ReturnType<typeof setInterval> = setInterval(() => {
        const debouncedImpressions = this.getDelayedProductImpressions().forEach((productList, listName) => {
            if (productList.length <= 0) {
                return;
            }

            chunk(productList, this.delayedProductImpressionsChunkSize).forEach((productsImpressions) => {
                this.productsImpression(listName, productsImpressions);
            });

            this.resetDelayedProductImpressions(listName);
        });
    }, this.delayedProductImpressionsInterval);

    readonly delayedPromotionImpressionsLoop: ReturnType<typeof setInterval> = setInterval(() => {
        const debouncedImpressions = this.getDelayedPromotionImpressions().forEach((promotionList, listName) => {
            if (promotionList.length <= 0) {
                return;
            }

            chunk(promotionList, this.delayedPromotionImpressionsChunkSize).forEach((promotionsImpressions) => {
                this.promotionsImpression(promotionsImpressions);
            });

            this.resetDelayedPromotionImpressions(listName);
        });
    }, this.delayedPromotionImpressionsInterval);

    currencyCode: string;

    constructor(eventPrefix: string, currencyCode: string = 'USD') {
        this.EVENT_PREFIX = eventPrefix;

        this.currencyCode = currencyCode;
    }

    setCurrencyCode(currencyCode: string): void {
        this.currencyCode = currencyCode;
    }

    push(data: any) {
        if (arguments.length == 1) {
            window.dataLayer.push(...arguments);
        } else {
            window.dataLayer.push(arguments);
        }

        this.logger.log('push', Array.isArray(data) && data.length === 1 ? data[0] : data);
    }

    event(category: string, action: string): void {
        this.push({
            'event': `${this.EVENT_PREFIX}Event`,
            'eventCat': category,
            'eventAct': action,
        });
    }

    eventLab(category: string, action: string, label: string): void {
        this.push({
            'event': `${this.EVENT_PREFIX}EventLab`,
            'eventCat': category,
            'eventAct': action,
            'eventLab': label,
        });
    }

    socialInt(network: string, action: string, target: string): void {
        this.push({
            'event': 'socialInt',
            'socialNet': network,
            'socialAct': action,
            'socialTarget': target,
        });
    }

    santianoEcommerce(params: TDataLayerEventCommand): void {
        this.push({
            'event': 'santianoEcommerce',
            ...params,
        });
    }

    productsImpression(listName: string, productList: TDataLayerProduct[]): void {
        this.santianoEcommerce({
            'eventCommand': 'productImpression',
            'currencyCode': this.currencyCode,
            'productImpressionListName': listName,
            'productList': productList,
        });
    }

    productImpression(listName: string, product: TDataLayerProduct): void {
        this.productsImpression(listName, [product]);
    }

    productClick(listName: string, product: TDataLayerProduct): void {
        this.santianoEcommerce({
            'eventCommand': 'productClick',
            'currencyCode': this.currencyCode,
            'productImpressionListName': listName,
            'productList': [product],
        });
    }

    productDetail(product: TDataLayerProduct): void {
        this.santianoEcommerce({
            'eventCommand': 'productDetail',
            'currencyCode': this.currencyCode,
            'productList': [product],
        });
    }

    cartProductAdded(product: TDataLayerProduct, quantity: number = 1): void {
        product.productQuantity = quantity;

        this.santianoEcommerce({
            'eventCommand': 'cartProductAdded',
            'currencyCode': this.currencyCode,
            'productList': [product],
        });
    }

    cartProductRemoved(product: TDataLayerProduct, quantity: number = 1): void {
        product.productQuantity = quantity;

        this.santianoEcommerce({
            'eventCommand': 'cartProductRemoved',
            'currencyCode': this.currencyCode,
            'productList': [product],
        });
    }

    promotionsImpression(promotionList: TDataLayerPromotion[]): void {
        this.santianoEcommerce({
            'eventCommand': 'promotionImpression',
            'currencyCode': this.currencyCode,
            'promotionList': [promotionList],
        });
    }

    promotionImpression(promotion: TDataLayerPromotion): void {
        this.promotionsImpression([promotion]);
    }

    promotionClick(listName: string, promotion: TDataLayerPromotion): void {
        this.santianoEcommerce({
            'eventCommand': 'promotionClick',
            'promotionList': [promotion],
        });
    }

    checkoutStep(stepNumber: number, stepName: string, productList: TDataLayerProduct[]): void {
        this.santianoEcommerce({
            'eventCommand': 'checkoutStep',
            'currencyCode': this.currencyCode,
            'checkoutStepNumber': stepNumber,
            'checkoutStepName': stepName,
            'productList': productList,
        });
    }

    checkoutStepClick(stepNumber: number, stepName: string, productList: TDataLayerProduct[], checkoutStepOption?: string): void {
        const params: TDataLayerEventCommand = {
            'eventCommand': 'checkoutStep',
            'currencyCode': this.currencyCode,
            'checkoutStepNumber': stepNumber,
            'checkoutStepName': stepName,
            'productList': productList,
        };

        if (checkoutStepOption) {
            params.checkoutStepOption = checkoutStepOption;
        }

        this.santianoEcommerce(params);
    }

    checkoutOption(stepNumber: number, stepName: string, stepOption: string): void {
        this.santianoEcommerce({
            'eventCommand': 'checkoutOption',
            'currencyCode': this.currencyCode,
            'checkoutStepNumber': stepNumber,
            'checkoutStepName': stepName,
            'checkoutStepOption': stepOption,
        });
    }

    orderConfirmation(orderId: string, orderRevenue: number, orderTax: number, orderShippingCost: number, orderCoupon?: string, customerEmail?: string, productList?: TDataLayerProduct[]): void {
        this.santianoEcommerce({
            'eventCommand': 'orderConfirmation',
            'currencyCode': this.currencyCode,
            'orderId': orderId,
            'orderRevenue': orderRevenue,
            'orderTax': orderTax,
            'orderShippingCost': orderShippingCost,
            'orderCoupon': orderCoupon,
            'customerEmail': customerEmail,
            'productList': productList != null ? productList : [],
        });
    }

    delayedProductImpression(listName: string, product: TDataLayerProduct): void {
        let productList = this.delayedProductImpressions.get(listName);
        if (!Array.isArray(productList)) {
            productList = [];
        }

        productList.push(product);

        this.delayedProductImpressions.set(listName, productList);
    }

    delayedPromotionImpression(listName: string, promotion: TDataLayerPromotion): void {
        let promotionList = this.delayedPromotionImpressions.get(listName);
        if (!Array.isArray(promotionList)) {
            promotionList = [];
        }

        promotionList.push(promotion);

        this.delayedPromotionImpressions.set(listName, promotionList);
    }

    resetDelayedProductImpressions(listName: string): void {
        this.delayedProductImpressions.delete(listName);
    }

    getDelayedProductImpressions(): Map<string, TDataLayerProduct[]> {
        return this.delayedProductImpressions;
    }

    resetDelayedPromotionImpressions(listName: string): void {
        this.delayedPromotionImpressions.delete(listName);
    }

    getDelayedPromotionImpressions(): Map<string, TDataLayerPromotion[]> {
        return this.delayedPromotionImpressions;
    }
}

export {
    DataLayer,
    TDataLayerEventCommand,
    TDataLayerProduct,
    TDataLayerPromotion
};
